import { css } from '@emotion/css';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { RootState } from '../../../features';
import { actions as chatActions } from '../../../features/chat';
import { interpolateFreeText } from '../../../i18n';
import { sendEvent } from '../../../lib/google-analytics';
import { ChatErrorDialog } from '../../Chat/ChatErrorDialog';
import { ChatForm } from '../../Chat/ChatForm';
import { ChatWarnings } from '../../Chat/ChatWarnings';
import { PreviousMessageButton } from '../../Chat/PreviousMessageButton';
import { BackButton } from '../../shared/buttons/BackButton';
import { HintButton } from '../../shared/buttons/HintButton';
import { CharacterImage } from '../../shared/CharacterImage';
import { DialogRoute } from '../../shared/routes/DialogRoute';
import { MessageToAllHintDialog } from './MessageToAllHintDialog';
import { MessageToPersonHintDialog } from './MessageToPersonHintDialog';

const friendMessageStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #ffd0ae;
  color: #4d3906;

  header {
    flex: 0 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 52px;
    padding: 0 4px;
  }
  h1 {
    display: flex;
    align-items: center;
    font-size: 16px;
  }
  .image {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    margin-right: 6px;
  }
  .body {
    width: 100%;
    max-width: 480px;
    overflow-x: hidden;
    margin: 0 auto;
    padding: 8px 12px;
  }
`;

export const FriendMessage: React.FC = () => {
  const { t } = useTranslation(['best_friends', 'translation']);
  const routeMatch = useRouteMatch();
  const history = useHistory();
  const params = useParams<{ id?: string }>();
  const { friendItems } = useSelector((state: RootState) => state.bestFriends);
  const { sentLatest, errorTypes } = useSelector((state: RootState) => state.chat);
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(chatActions.reset());
    };
  }, [dispatch]);

  const hintPath = routeMatch ? `${routeMatch.url}/hint` : undefined;
  const errorDialogPath = routeMatch ? `${routeMatch.url}/error` : undefined;

  const isToAll = routeMatch && routeMatch.path === '/players/best-friends-message-to-all';
  const found = friendItems.find(friend => friend.id === params.id);

  const handleSubmit = useCallback(
    (message: string) => {
      const type = isToAll ? 'all_friend' : 'friend';
      const destination = found?.id;
      dispatch(chatActions.postMessage({ message, type, destination }));
      sendEvent('best_friends_send_message', 'submit', type);
    },
    [dispatch, isToAll, found]
  );

  const handleLatestMessageClick = useCallback(() => {
    const type = isToAll ? 'all_friend' : 'friend';
    dispatch(chatActions.fillPrevMessage());
    sendEvent('best_friends_resend_message', 'click', type);
  }, [dispatch, isToAll]);

  const handleHintClick = useCallback(() => {
    if (hintPath) {
      history.push(hintPath);
    }
  }, [hintPath, history]);

  if (routeMatch && routeMatch.url === history.location.pathname && errorTypes.length > 0) {
    return (
      <Redirect
        push={true}
        to={{ pathname: `${routeMatch.url}/error`, search: `?types=${errorTypes.join(',')}` }}
      />
    );
  }

  let title: string;
  let hintLabel: string;

  if (!isToAll && found) {
    title = found.isReported
      ? t('translation:reported_player')
      : interpolateFreeText(t, 'message_to_person', 'name', found.name);
    hintLabel = t('aria_help_message_to_person');
  } else {
    title = t('message_to_all');
    hintLabel = t('aria_help_message_to_all');
  }

  return (
    <>
      <main className={friendMessageStyles}>
        <header>
          <BackButton ariaLabel={t('aria_button_back')} />
          <h1>
            {found?.image && (
              <span className="image">
                <CharacterImage
                  src={found.image}
                  isPlayer={true}
                  isReported={found.isReported}
                  ariaHidden={true}
                />
              </span>
            )}
            {title}
          </h1>
          <HintButton ariaLabel={hintLabel} onClick={handleHintClick} />
        </header>
        <div className="body">
          <ChatForm onSubmit={handleSubmit} />
          {sentLatest && (
            <PreviousMessageButton message={sentLatest} onClick={handleLatestMessageClick} />
          )}
          <ChatWarnings />
        </div>
      </main>

      <DialogRoute path={errorDialogPath}>
        <ChatErrorDialog />
      </DialogRoute>
      <DialogRoute path={hintPath}>
        {isToAll ? <MessageToAllHintDialog /> : <MessageToPersonHintDialog />}
      </DialogRoute>
    </>
  );
};
